import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { CURRENCIES, fromMinor, toMinor } from '@/lib/money';
import type { FinanceCategory, FinanceFeeRule } from '@/api/generated/types.gen';

/** The fee rule as the form holds it: text the operator typed, not numbers. */
export interface FeeRuleDraft {
  enabled: boolean;
  percent: string;
  fixedMajor: string;
  fixedCurrency: string;
  categoryId: string;
}

const EMPTY_FEE_RULE_DRAFT: FeeRuleDraft = {
  enabled: false,
  percent: '',
  fixedMajor: '',
  fixedCurrency: '',
  categoryId: '',
};

/** 590 → "5.9", 600 → "6", 0 → "". */
function bpToPercent(bp: number): string {
  if (bp === 0) return '';
  const s = (bp / 100).toFixed(2);
  return s.replace(/\.?0+$/, '');
}

// eslint-disable-next-line react-refresh/only-export-components
export function feeRuleToDraft(r: FinanceFeeRule | null | undefined): FeeRuleDraft {
  if (!r) return { ...EMPTY_FEE_RULE_DRAFT };
  const cur = r.fixed_currency ?? '';
  return {
    enabled: true,
    percent: bpToPercent(r.percent_bp),
    fixedMajor: r.fixed_minor !== 0 && cur !== '' ? fromMinor(r.fixed_minor, cur) : '',
    fixedCurrency: cur,
    categoryId: r.category_id,
  };
}

/**
 * Disabled → null (the server reads null/absent as "no rule" and refuses an
 * all-zero rule with 422). Bad input comes back as { error }, never thrown.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function draftToFeeRule(d: FeeRuleDraft): FinanceFeeRule | null | { error: string } {
  if (!d.enabled) return null;

  const pctText = d.percent.trim().replace(',', '.');
  let percentBp = 0;
  if (pctText !== '') {
    const m = /^(\d+)(?:\.(\d*))?$/.exec(pctText);
    if (!m) return { error: 'Percent must be a number, e.g. 5.9.' };
    const frac = m[2] ?? '';
    if (frac.length > 2) return { error: 'Percent allows at most two decimals, e.g. 5.95.' };
    // Exact: integer digits only, no float arithmetic.
    percentBp = Number.parseInt(m[1] ?? '0', 10) * 100 + Number.parseInt(frac.padEnd(2, '0') || '0', 10);
    if (percentBp > 9999) return { error: 'Percent must be below 100.' };
  }

  let fixedMinor = 0;
  let fixedCurrency: string | undefined;
  if (d.fixedMajor.trim() !== '') {
    // The amount is parsed in the chosen currency; without one, only a zero
    // (nothing to send) is acceptable.
    if (!d.fixedCurrency) {
      if (!/^0*([.,]0*)?$/.test(d.fixedMajor.trim())) return { error: 'Pick a currency for the fixed part.' };
    } else {
      const m = toMinor(d.fixedMajor, d.fixedCurrency);
      if (m === null || m < 0) return { error: `Fixed part is not a valid ${d.fixedCurrency} amount.` };
      fixedMinor = m;
      if (m > 0) fixedCurrency = d.fixedCurrency;
    }
  }

  if (percentBp === 0 && fixedMinor === 0) {
    return { error: 'Set a percent or a fixed part, or turn the rule off.' };
  }
  if (!d.categoryId) return { error: 'Pick the category the fee is booked under.' };

  return {
    percent_bp: percentBp,
    fixed_minor: fixedMinor,
    ...(fixedCurrency ? { fixed_currency: fixedCurrency } : {}),
    category_id: d.categoryId,
  };
}

/** Server rule (§3.2): active expense, no counterparty, not platform_fee. */
function usableCategory(c: FinanceCategory): boolean {
  return c.is_active && c.kind === 'expense' && !c.requires_counterparty && c.code !== 'platform_fee';
}

export interface AccountFeeRuleFieldsProps {
  value: FeeRuleDraft;
  onChange: (d: FeeRuleDraft) => void;
  categories: FinanceCategory[];
  /** Validation message from draftToFeeRule, shown under the fields. */
  error?: string;
}

export function AccountFeeRuleFields({ value, onChange, categories, error }: AccountFeeRuleFieldsProps) {
  const options = categories
    .filter((c) => usableCategory(c) || c.id === value.categoryId)
    .map((c) => ({ value: c.id, label: usableCategory(c) ? c.name : `${c.name} (not usable — pick another)` }));
  return (
    <fieldset className="flex flex-col gap-3">
      <Checkbox
        label="Fee rule (suggestion only)"
        checked={value.enabled}
        onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
      />
      <p className="text-xs text-[var(--text-muted)]">
        Suggests a withheld line for incoming settles. Nothing is booked from it.
      </p>
      {value.enabled && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Percent (%)"
              inputMode="decimal"
              value={value.percent}
              onChange={(e) => onChange({ ...value, percent: e.target.value })}
            />
            <Input
              label="Fixed part"
              inputMode="decimal"
              value={value.fixedMajor}
              onChange={(e) => onChange({ ...value, fixedMajor: e.target.value })}
            />
            <Select
              label="Currency"
              value={value.fixedCurrency}
              placeholder="—"
              options={[...CURRENCIES]}
              onChange={(e) => onChange({ ...value, fixedCurrency: e.target.value })}
            />
          </div>
          <Select
            label="Category"
            value={value.categoryId}
            placeholder="Choose a category"
            options={options}
            onChange={(e) => onChange({ ...value, categoryId: e.target.value })}
          />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </>
      )}
    </fieldset>
  );
}
